import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import isNullOrUndefined from 'scaleflow/utils/isNullOrUndefined';
import { BON_INIT } from './back-of-a-napkin';

export default class BacklogInitiativesInitiativeInvestmentsController extends Controller {
  @service store;

  @tracked editMode = false;
  @tracked showInvestmentConfidenceSlideover = false;
  @tracked showPpwSlideover = false;
  @tracked bon = {
    value: undefined,
    notes: '',
    text_delta: {
      ops: [],
    },
    investments: {
      value: 0,
      calculations: [],
    },
    returns: {
      value: 0,
      calculations: [],
    },
  };

  @action
  updateBonInvestments(investments) {
    const bon = this.bon;
    if (bon.investments == undefined)
      bon.investments = { value: 0, calculations: [] };
    bon.investments.calculations = investments.calculations;
    bon.investments.value = investments.calculations.reduce((acc, curr) => {
      return acc + (curr?.value || 0) * (curr?.confidence_value || 0);
    }, 0);
    const bonValue = this.calculateBonValue(bon);

    bon.value = bonValue;
    this.bon = bon;
  }

  calculateBonValue(bon) {
    if (
      (isNullOrUndefined(bon?.returns) ||
        (bon.returns?.value === 0 &&
          bon.returns?.calculations?.length === 0)) &&
      (isNullOrUndefined(bon?.investments) ||
        (bon.investments?.value === 0 &&
          bon.investments?.calculations?.length === 0))
    )
      return undefined;
    else return (+bon.returns?.value || 0) - (+bon.investments?.value || 0);
  }

  @action
  bonHasIncompleteTemplates() {
    const investments = this.bon.investments?.calculations;
    let formsHaveError = [];
    investments?.forEach((item, index) => {
      if (item.type === 'custom')
        formsHaveError.push(
          this.customTemplateValidator('Investment', item, index),
        );
      if (item.type === 'investment' || item.type === 'person-weeks-role')
        formsHaveError.push(
          this.investmentTemplateValidator('Investment', item, index),
        );
    });

    return formsHaveError.includes(true);
  }

  @action
  toggleInvestmentConfidenceSlideover(flag) {
    if (flag !== undefined && typeof flag === 'boolean')
      this.showInvestmentConfidenceSlideover = flag;
    else
      this.showInvestmentConfidenceSlideover =
        !this.showInvestmentConfidenceSlideover;
  }

  @action
  togglePpwSlideover(flag) {
    if (flag !== undefined && typeof flag === 'boolean')
      this.showPpwSlideover = flag;
    else this.showPpwSlideover = !this.showPpwSlideover;
  }

  @action
  formatBon() {
    const bon = this.bon;
    if (bon.investments?.calculations) {
      bon.investments?.calculations.forEach((item) => {
        if (item.type === 'custom') return;
        if (item.type === 'investment' || item.type === 'person-weeks-role') {
          const value = +item?.price_person_week * +item?.weeks * +item?.people;
          item.value = isNaN(value) ? null : value;
        }
      });
    }

    if (bon.returns?.calculations) {
      bon.returns?.calculations.forEach((item) => {
        if (item.type === 'custom') return;
        if (item.type === 'cost-fte') {
          const value =
            +item?.ftes * +item?.price_person_week * +item?.horizon * 4;
          item.value = isNaN(value) ? null : value;
        }
        if (item.type === 'new-customers') {
          const value = +item?.new_customers * +item?.bon_clv;
          item.value = isNaN(value) ? null : value;
        }
        if (item.type === 'upsell') {
          const value = +item?.customers * +item.upsell_value;
          item.value = isNaN(value) ? null : value;
        }
      });
    }
    if (
      bon?.text_delta &&
      bon?.text_delta?.ops &&
      bon?.text_delta?.ops?.length === 1 &&
      Object.keys(bon?.text_delta?.ops?.[0])?.length === 0
    ) {
      bon.text_delta = {
        ops: [],
      };
    }
  }

  customTemplateValidator(type, item, index) {
    let hasErrors = false;
    if (item.description === '' || !item.description) {
      hasErrors = true;
      document
        .getElementById(`${type}-custom-description-${index}`)
        .classList.add('input--error');
    } else
      document
        .getElementById(`${type}-custom-description-${index}`)
        .classList.remove('input--error');
    if (item.value === '' || isNaN(+item.value)) {
      hasErrors = true;
      document
        .getElementById(`${type}-custom-value-${index}`)
        .classList.add('input--error');
    } else
      document
        .getElementById(`${type}-custom-value-${index}`)
        .classList.remove('input--error');

    return hasErrors;
  }

  investmentTemplateValidator(type, item, index) {
    let hasErrors = false;
    if (item.weeks === '' || isNaN(item.weeks)) {
      hasErrors = true;
      document
        .getElementById(`${type}-weeks-${index}`)
        .classList.add('input--error');
    } else
      document
        .getElementById(`${type}-weeks-${index}`)
        .classList.remove('input--error');
    if (item.people === '' || isNaN(item.people)) {
      hasErrors = true;
      document
        .getElementById(`${type}-people-${index}`)
        .classList.add('input--error');
    } else
      document
        .getElementById(`${type}-people-${index}`)
        .classList.remove('input--error');

    return hasErrors;
  }

  @action
  toggleEditMode(val = undefined) {
    if (val === undefined) this.editMode = !this.editMode;
    else this.editMode = val;
    this.formatBon();
  }

  @action
  async resetBon() {
    if (!this.bon?.id) {
      // New unsaved record — discard it and create a fresh one so
      // `this.bon` remains a store record (with .save()) after cancel.
      if (this.bon && typeof this.bon.unloadRecord === 'function') {
        this.bon.unloadRecord();
      }
      this.bon = this.store.createRecord('bon', {
        initiative: this.model,
        value: BON_INIT.value,
        investments: { value: 0, calculations: [] },
        returns: { value: 0, calculations: [] },
        notes: BON_INIT.notes,
      });
    } else {
      this.bon.rollbackAttributes();
      const bon = await this.store.findRecord('bon', this.bon.id, {
        reload: true,
      });
      this.bon = bon;
    }
    this.toggleEditMode();
  }

  @action
  handleSave(e) {
    e.preventDefault();
    if (this.bonHasIncompleteTemplates()) return;
    this.bon.initiative = this.model;
    this.bon.value = this.calculateBonValue(this.bon);
    this.bon.save().then(() => {
      this.toggleEditMode();
      this.model.reload();
    });
  }
}
